import {
  Shield,
  BadgeCheck,
  Banknote,
  CloudRain,
  Home,
  Layers,
} from "lucide-react";
import { AreaPageShell } from "./AreaPageShell";

export interface AsphaltFaq {
  question: string;
  answer: string;
}

export interface AsphaltPageData {
  city: string;
  citySlug: string;
  serviceName: string;
  title: string;
  description: string;
  h1: string;
  heroIntro: string;
  climateText: string;
  localPresenceText: string;
  warrantyText: string;
  pricingText: string;
  priceFactorsText: string;
  financingText: string;
  step1: string;
  step2: string;
  step3: string;
  step4: string;
  architecturalText: string;
  brandsText: string;
  ratedText: string;
  faqs: AsphaltFaq[];
}

const buildSchema = (data: AsphaltPageData) => {
  const base = "https://www.saskroofingcompany.com";
  const url = `${base}/areas/${data.citySlug}/asphalt`;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: data.faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "RoofingContractor",
    "@id": `${base}/#localbusiness`,
    name: "Sask Roofing Company",
    url: `${base}/`,
    telephone: "+1-639-739-7969",
    email: "info@saskroofingcompany.com",
    areaServed: {
      "@type": "City",
      name: data.city,
      containedInPlace: { "@type": "State", name: "Saskatchewan" },
    },
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${url}#service`,
    serviceType: "Asphalt Shingle Roofing",
    name: "Asphalt Shingle Roofing",
    description: data.description,
    url,
    provider: {
      "@id": `${base}/#localbusiness`,
      "@type": "RoofingContractor",
      name: "Sask Roofing Company",
      url: `${base}/`,
      telephone: "+1-639-739-7969",
    },
    areaServed: { "@type": "City", name: data.city },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "CAD",
      lowPrice: "6000",
      highPrice: "15000",
    },
    availableChannel: {
      "@type": "ServiceChannel",
      serviceUrl: `${base}/contact`,
      servicePhone: {
        "@type": "ContactPoint",
        telephone: "+1-639-739-7969",
        contactType: "customer service",
        areaServed: "CA-SK",
        availableLanguage: "English",
      },
    },
    termsOfService: `${base}/about#warranty`,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "87",
      bestRating: "5",
      worstRating: "1",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${base}/` },
      {
        "@type": "ListItem",
        position: 2,
        name: "Service Areas",
        item: `${base}/areas`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: data.city,
        item: `${base}/areas/${data.citySlug}`,
      },
      {
        "@type": "ListItem",
        position: 4,
        name: "Asphalt Shingle Roofing",
        item: url,
      },
    ],
  };

  return [breadcrumbSchema, localBusinessSchema, serviceSchema, faqSchema];
};

export function AsphaltPage({ data }: { data: AsphaltPageData }) {
  return (
    <AreaPageShell
      data={{
        ...data,
        schema: buildSchema(data),
        canonical: `/areas/${data.citySlug}/asphalt`,
      }}
      heroImage="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/67bfca73-3cfe-4fd0-b9fd-9ed4f4780799.png&output=webp&q=60"
      heroAlt={`Asphalt shingle roofing in ${data.city}`}
      whyBlocks={[
        {
          icon: CloudRain,
          title: `Asphalt shingle roofing built for ${data.city} weather`,
          text: data.climateText,
        },
        {
          icon: Shield,
          title: `Local asphalt roofing crews in ${data.city}`,
          text: data.localPresenceText,
        },
        {
          icon: BadgeCheck,
          title: `Asphalt shingle warranties in ${data.city}`,
          text: data.warrantyText,
        },
      ]}
      costBlocks={[
        {
          title: `Asphalt roofing price range in ${data.city}`,
          text: data.pricingText,
        },
        {
          title: `What affects asphalt roof cost in ${data.city}`,
          text: data.priceFactorsText,
        },
        {
          title: `Asphalt roofing financing in ${data.city}`,
          text: data.financingText,
        },
      ]}
      extraCostTiles={[
        {
          label: "Asphalt replacement",
          value: "$6K–$15K",
          note: "typical Saskatchewan home",
        },
        {
          label: "Lifespan",
          value: "20–30 yrs",
          note: "architectural shingles",
        },
        {
          label: "Warranty",
          value: "25–50 yrs",
          note: "manufacturer material",
        },
      ]}
      extraCallout={{
        icon: Banknote,
        title: `Fixed-price asphalt roofing quotes in ${data.city}`,
        text: "The price on your contract is the price you pay. No 'unforeseen extras' halfway through the job. We've never charged a customer a dollar over their written quote.",
      }}
      processSteps={[
        {
          title: `Free inspection & tear-off decision in ${data.city}`,
          text: data.step1,
        },
        {
          title: `Asphalt shingle selection & quote in ${data.city}`,
          text: data.step2,
        },
        {
          title: `Tear-off & underlayment installation in ${data.city}`,
          text: data.step3,
        },
        {
          title: `Shingle install & cleanup in ${data.city}`,
          text: data.step4,
        },
      ]}
      materialBlocks={[
        {
          icon: Layers,
          title: `Architectural vs 3-tab shingles in ${data.city}`,
          text: data.architecturalText,
        },
        {
          icon: Home,
          title: `IKO, BP & GAF shingle lines in ${data.city}`,
          text: data.brandsText,
        },
        {
          icon: CloudRain,
          title: `Wind & hail-rated shingles in ${data.city}`,
          text: data.ratedText,
        },
      ]}
    />
  );
}
